// Lightweight UTM capture/persistence helpers.
//
// Strategy:
//   1. On landing page mount, read utm_* params from URL.
//   2. If present, persist to sessionStorage so they survive any
//      auth confirmation redirect (e.g. ?confirmation_url=...).
//   3. On signup, read from URL first, fall back to sessionStorage,
//      and pass into supabase.auth.signUp options.data - the
//      `create_supplier_on_signup` trigger copies them onto suppliers.

const STORAGE_KEY = "eafl_utm";

export interface UtmData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  signup_referrer?: string;
}

const KEYS: (keyof UtmData)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

/** Read UTMs from the current URL. */
export function readUtmFromUrl(search?: string): UtmData {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(search ?? window.location.search);
  const out: UtmData = {};
  for (const k of KEYS) {
    const v = params.get(k);
    if (v) out[k] = v.slice(0, 200);
  }
  return out;
}

/** Persist UTMs (and referrer) to sessionStorage if any are present. */
export function captureUtmFromUrl(): void {
  if (typeof window === "undefined") return;
  const fromUrl = readUtmFromUrl();
  if (Object.keys(fromUrl).length === 0) return;

  const referrer = document.referrer || undefined;
  const payload: UtmData = {
    ...fromUrl,
    ...(referrer ? { signup_referrer: referrer.slice(0, 500) } : {}),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Get UTMs to attach to a signup payload (URL > sessionStorage). */
export function getSignupUtm(): UtmData {
  if (typeof window === "undefined") return {};
  const fromUrl = readUtmFromUrl();
  if (Object.keys(fromUrl).length > 0) {
    const referrer = document.referrer || undefined;
    return {
      ...fromUrl,
      ...(referrer ? { signup_referrer: referrer.slice(0, 500) } : {}),
    };
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as UtmData;
  } catch {
    /* ignore */
  }
  return {};
}

/** Clear after successful signup (optional). */
export function clearStoredUtm(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
